import React, { useEffect, useState } from "react";
import {
    Stack,
    TextField,
    FormControl,
    FormControlLabel,
    Select,
    MenuItem,
    RadioGroup,
    Radio,
    Button,
    Typography,
    ButtonBase,
} from "@mui/material";

import { CustomDateButton } from "./EventCreateForm";

import { dayjsUTC, addDaySuffix, weekDays } from "../../helpers/dates";
import Icons from "../../helpers/icons";
import { buildRRule, rruleToEvent } from "../../helpers/events";
import Colours from "../../helpers/colours";

const dayjs = dayjsUTC;

const RadioStyle = {
    pl: 0,
};

const EventRepeatForm = ({
    start,
    rrule = "",
    onChange,
    hydrate = true,
    setErrorRecurrence,
}) => {
    const isEdit = rrule !== "";
    const [isHydrating, setIsHydrating] = useState(hydrate);

    // Starts
    const [startDate, setStartDate] = useState(start);
    // Stop Repeating after
    const [endDate, setEndDate] = useState(dayjs(start).add(3, "months"));

    const [errorEndDate, setErrorEndDate] = useState("");

    // Date data
    const [dayOfMonth, setDayOfMonth] = useState(null);
    const [weekOfMonth, setWeekOfMonth] = useState(null);
    const [weekDayName, setWeekDayName] = useState(null);
    const [monthName, setMonthName] = useState(null);

    // Repeat every
    const [repeatEvery, setRepeatEvery] = useState(1);
    const [repeatUnit, setRepeatUnit] = useState("Month");

    // Suboptions
    const [weeklyDays, setWeeklyDays] = useState([]);
    const [monthlyOption, setMonthlyOption] = useState("dayOfMonth"); // dayOfMonth, weekDay, *lastDay, *lastWeekday
    const [yearlyOption, setYearlyOption] = useState("specificDate"); // specificDate, weekDay, *lastWeekday

    useEffect(() => {
        // Save changes to the final rrule
        if (!validate() || isHydrating) return;
        handleSave();
    }, [
        startDate,
        endDate,
        repeatEvery,
        repeatUnit,
        weeklyDays,
        monthlyOption,
        yearlyOption,
        dayOfMonth,
        weekOfMonth,
        weekDayName,
        monthName,
    ]);

    useEffect(() => {
        if (!isHydrating) handleSave();
    }, [isHydrating]);

    const handleSave = () => {
        const data = {
            startDate,
            repeatEvery,
            repeatUnit,
            options: {},
            endDate,
        };

        // WEEK
        if (repeatUnit === "Week") {
            data.options.weeklyDays = weeklyDays?.length
                ? weeklyDays
                : [weekDayName];
        }

        // MONTH
        else if (repeatUnit === "Month") {
            data.options.monthlyOption = monthlyOption;

            if (monthlyOption === "dayOfMonth") {
                data.options.day = dayOfMonth;
            } else {
                data.options.pattern = {
                    week: weekOfMonth,
                    day: weekDayName,
                };
            }
        }

        // YEAR
        else if (repeatUnit === "Year") {
            data.options.yearlyOption = yearlyOption;

            if (yearlyOption === "specificDate") {
                data.options.date = dayjs(startDate);
            } else {
                data.options.pattern = {
                    week: weekOfMonth,
                    day: weekDayName,
                    month: monthName,
                };
            }
        }

        // Build RRULE
        let newRRule = buildRRule(data);

        const modifiedFields = getModifiedFields(newRRule);

        // Preserve EXDATES if reccurrence didn't change
        if (!modifiedFields.recurrence) {
            newRRule = buildRRule(data, rrule);
        }

        onChange?.(newRRule, modifiedFields.endDate ? endDate : null);
    };

    const handleWeeklyDayToggle = (day) => {
        setWeeklyDays((prev) =>
            prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
        );
    };

    const validate = (end = endDate) => {
        let isValid = true;
        if (startDate > end) {
            setErrorEndDate("Must be greater than or equal to the Event Date.");
            isValid = false;
        } else if (repeatUnit === "Day" && end.diff(startDate, "day") > 365) {
            setErrorEndDate(
                "Daily recurrence can be configured for a maximum of 365 days only.",
            );
            isValid = false;
        } else if (repeatUnit === "Month" && end.diff(startDate, "year") > 5) {
            setErrorEndDate(
                "Monthly recurrence can be configured for a maximum period of 5 years only.",
            );
            isValid = false;
        } else if (repeatUnit === "Year" && end.diff(startDate, "year") > 10) {
            setErrorEndDate(
                "Yearly recurrence can be configured for a maximum period of 10 years only.",
            );
            isValid = false;
        } else {
            setErrorEndDate("");
        }

        setErrorRecurrence(!isValid);
        return isValid;
    };

    const getModifiedFields = (newRRule) => {
        const modifiedFields = {};

        if (!isEdit) {
            modifiedFields.startDate = startDate;
            modifiedFields.endDate = endDate;
            modifiedFields.recurrence = true;
            return modifiedFields;
        }

        const oldData = rruleToEvent(rrule);
        const newData = rruleToEvent(newRRule);

        if (
            newData.startDate.format("YYYY-MM-DD") !==
            oldData.startDate.format("YYYY-MM-DD")
        )
            modifiedFields.startDate = newData.startDate;

        if (
            newData.endDate.format("YYYY-MM-DD") !==
            oldData.endDate.format("YYYY-MM-DD")
        )
            modifiedFields.endDate = newData.endDate;

        if (
            oldData.repeatEvery !== newData.repeatEvery ||
            oldData.repeatUnit !== newData.repeatUnit ||
            JSON.stringify(oldData.options) !== JSON.stringify(newData.options)
        ) {
            modifiedFields.recurrence = true;
        }

        return modifiedFields;
    };

    useEffect(() => {
        // Populate options when first rendering the component

        const eventData = !rrule || rrule === "" ? {} : rruleToEvent(rrule);

        const auxStart = start || eventData.startDate;

        let {
            auxWeekDayName,
            auxDayOfMonth,
            auxWeekOfMonth,
            auxMonthName,
            auxEndDate,
        } = getDefaultFromStartDate(auxStart);

        setDayOfMonth(auxDayOfMonth);
        setWeekOfMonth(auxWeekOfMonth);
        setWeekDayName(auxWeekDayName);
        setMonthName(auxMonthName);

        // Populate with main fields
        setStartDate(auxStart);
        setRepeatEvery(eventData.repeatEvery || 1);
        setRepeatUnit(eventData.repeatUnit || "Month");

        // --- SUBOPTIONS ---
        setWeeklyDays(eventData.options?.weeklyDays || [auxWeekDayName]);
        setYearlyOption(eventData.options?.yearlyOption || "specificDate");
        const auxMonthlyOption =
            eventData.options?.monthlyOption || "dayOfMonth";
        if (
            (auxDayOfMonth < 28 && auxMonthlyOption === "lastDay") ||
            (auxDayOfMonth <= 21 && auxMonthlyOption === "lastWeekDay")
        )
            setMonthlyOption("dayOfMonth");
        else setMonthlyOption(auxMonthlyOption);

        // --- END CONDITIONS ---
        setEndDate(eventData.endDate || auxEndDate);

        setTimeout(() => setIsHydrating(false), 0);
    }, [rrule, start]);

    const getDefaultFromStartDate = (start) => {
        if (!start) return;

        const auxWeekDayName = start.format("dddd");
        const auxDayOfMonth = start.date();
        const auxWeekOfMonth = Math.ceil(auxDayOfMonth / 7);
        const auxMonthName = start.format("MMMM");
        const auxEndDate = dayjs(start).add(3, "months");

        return {
            auxWeekDayName,
            auxDayOfMonth,
            auxWeekOfMonth,
            auxMonthName,
            auxEndDate,
        };
    };

    return (
        <Stack spacing={3}>
            {/* REPEAT EVERY */}
            <Stack direction="column" spacing={2}>
                <Typography variant="subtitle1" fontWeight={"bold"}>
                    Repeats
                </Typography>

                <Stack
                    direction={"row"}
                    sx={{
                        justifyItems: "space-between",
                    }}
                    spacing={1}
                >
                    <TextField
                        id="eventform-recurrence-number-textfield"
                        type="number"
                        value={repeatEvery}
                        onChange={(e) => {
                            let num = Number(e.target.value);
                            if (num > 365) num = 365;
                            if (num < 1) num = 1;
                            setRepeatEvery(num);
                        }}
                        inputProps={{ min: 1, max: 365 }}
                        sx={{ width: "8.5vh" }}
                    />
                    <Select
                        value={repeatUnit}
                        onChange={(e) => setRepeatUnit(e.target.value)}
                        sx={{ width: "auto" }}
                    >
                        <MenuItem value="Day">Day</MenuItem>
                        <MenuItem value="Week">Week</MenuItem>
                        <MenuItem value="Month">Month</MenuItem>
                        <MenuItem value="Year">Year</MenuItem>
                    </Select>
                </Stack>
            </Stack>

            {/* WEEKLY SUBOPTIONS */}
            {repeatUnit === "Week" && (
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        flexWrap: "wrap",
                        marginTop: 2,
                        mt: 2,
                        // justifyContent: "center",
                    }}
                >
                    {weekDays.map((day) => {
                        const selected = weeklyDays.includes(day);
                        return (
                            <ButtonBase
                                key={day}
                                onClick={() => handleWeeklyDayToggle(day)}
                                sx={{
                                    width: 50,
                                    height: 50,
                                    borderRadius: "50%",
                                    border: "1px solid",
                                    borderColor: selected
                                        ? "primary.main"
                                        : "grey.400",
                                    backgroundColor: selected
                                        ? "primary.main"
                                        : "transparent",
                                    color: selected ? "white" : "text.primary",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        backgroundColor: selected
                                            ? "primary.dark"
                                            : "grey.100",
                                    },
                                }}
                            >
                                <Typography variant="subtitle2">
                                    {day.slice(0, 3)}
                                </Typography>
                            </ButtonBase>
                        );
                    })}
                </Stack>
            )}

            {/* MONTHLY SUBOPTIONS */}
            {repeatUnit === "Month" && (
                <FormControl component="fieldset" sx={{ marginTop: 2 }}>
                    <RadioGroup>
                        <Stack
                            direction={"column"}
                            spacing={1}
                            sx={{
                                justifyContent: "space-between",
                            }}
                        >
                            <FormControlLabel
                                value="dayOfMonth"
                                control={
                                    <Radio
                                        checked={monthlyOption === "dayOfMonth"}
                                        onClick={() =>
                                            setMonthlyOption("dayOfMonth")
                                        }
                                        sx={{ ...RadioStyle }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            flexGrow: 1,
                                        }}
                                        variant="subtitle2"
                                    >
                                        On day {dayOfMonth}
                                    </Typography>
                                }
                            />
                            {dayOfMonth >= 28 && (
                                <FormControlLabel
                                    value="lastDay"
                                    control={
                                        <Radio
                                            checked={
                                                monthlyOption === "lastDay"
                                            }
                                            onClick={() =>
                                                setMonthlyOption("lastDay")
                                            }
                                            sx={{ ...RadioStyle }}
                                        />
                                    }
                                    label={
                                        <Typography
                                            sx={{
                                                flexGrow: 1,
                                            }}
                                            variant="subtitle2"
                                        >
                                            On the last day
                                        </Typography>
                                    }
                                />
                            )}
                            <FormControlLabel
                                value="weekdayPattern"
                                control={
                                    <Radio
                                        checked={
                                            monthlyOption === "weekdayPattern"
                                        }
                                        onClick={() =>
                                            setMonthlyOption("weekdayPattern")
                                        }
                                        sx={{ ...RadioStyle }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            flexGrow: 1,
                                        }}
                                        variant="subtitle2"
                                    >
                                        On the {addDaySuffix(weekOfMonth)}{" "}
                                        {weekDayName}
                                    </Typography>
                                }
                            />
                            {dayOfMonth > 21 && (
                                <FormControlLabel
                                    value="lastWeekDay"
                                    control={
                                        <Radio
                                            checked={
                                                monthlyOption === "lastWeekDay"
                                            }
                                            onClick={() =>
                                                setMonthlyOption("lastWeekDay")
                                            }
                                            sx={{ ...RadioStyle }}
                                        />
                                    }
                                    label={
                                        <Typography
                                            sx={{
                                                flexGrow: 1,
                                            }}
                                            variant="subtitle2"
                                        >
                                            On the last {weekDayName}
                                        </Typography>
                                    }
                                />
                            )}
                        </Stack>
                    </RadioGroup>
                </FormControl>
            )}

            {/* YEARLY SUBOPTIONS */}
            {repeatUnit === "Year" && (
                <FormControl component="fieldset" sx={{ marginTop: 2 }}>
                    <RadioGroup>
                        <Stack
                            direction={"column"}
                            spacing={1}
                            sx={{
                                justifyContent: "space-between",
                            }}
                        >
                            <FormControlLabel
                                value="specificDate"
                                control={
                                    <Radio
                                        checked={
                                            yearlyOption === "specificDate"
                                        }
                                        onClick={() =>
                                            setYearlyOption("specificDate")
                                        }
                                        sx={{ ...RadioStyle }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            flexGrow: 1,
                                        }}
                                        variant="subtitle2"
                                    >
                                        On {monthName}{" "}
                                        {addDaySuffix(dayOfMonth)}
                                    </Typography>
                                }
                            />

                            <FormControlLabel
                                value="weekdayPattern"
                                control={
                                    <Radio
                                        checked={
                                            yearlyOption === "weekdayPattern"
                                        }
                                        onClick={() =>
                                            setYearlyOption("weekdayPattern")
                                        }
                                        sx={{ ...RadioStyle }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            flexGrow: 1,
                                        }}
                                        variant="subtitle2"
                                    >
                                        On the {addDaySuffix(weekOfMonth)}{" "}
                                        {weekDayName} of {monthName}
                                    </Typography>
                                }
                            />
                        </Stack>
                    </RadioGroup>
                </FormControl>
            )}

            {/* END REPEAT */}
            <CustomDateButton
                label="Ends on"
                direction="column"
                required={true}
                date={endDate}
                error={startDate > endDate || errorEndDate !== ""}
                errorText={errorEndDate}
                onChange={(v) => {
                    validate(v);
                    setEndDate(v);
                }}
                minDate={startDate}
                maxDate={dayjs(startDate).add(10, "year")}
            />
        </Stack>
    );
};

export default EventRepeatForm;
